"use client";
import React, { useCallback, useEffect, useState } from "react";
import "./styles.css";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  Dropdown,
  Field,
  Link,
  Spinner,
  Toast,
  Toaster,
  ToastTitle,
  ToastTrigger,
  useId,
  useToastController,
} from "@fluentui/react-components";
import ClientSelection from "./Clients";
import apiServices from "../../../service";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import TemplateSelectionModal from "./TemplateSelectionModal";
import OverlayLoader from "../../../components/OverlayLoader";
import store from "../../../redux/store";
import axios from "axios";
import { useDispatch } from "react-redux";
import ASCButton from "../../../components/Button";
import TextEditor from "../../../components/TextEditor";
import { stripHtml } from "../../../utils";

type selectedValue = {
  customer_id: string;
  first_names: string;
  last_name: string;
};

const MAX_NOTE_LENGTH = 5000;

const LetterGeneration = () => {
  const [showClientSelection, setShowClientSelection] = useState(false);
  const [clients, setClients] = useState<selectedValue[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState<selectedValue[]>([]);
  const [templateListing, setTemplateListing] = useState<any>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>("");
  const [openSampleTemplate, setOpenSampleTemplate] = useState<boolean>(false);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState<any>("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [sampleTemplate, setSampleTemplate] = useState<any>([]);
  const [createContactHistory, setCreateContactHistory] = useState(false);
  const [contactHistoryNote, setContactHistoryNote] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);

  // 1. New tracked state to monitor if the cursor has interacted with the text area
  const [isNoteTouched, setIsNoteTouched] = useState<boolean>(false);

  const dispatch = useDispatch();
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  const notify = (message?: any, type?: any) =>
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <ToastTrigger>
              <Link>Dismiss</Link>
            </ToastTrigger>
          }
        >
          {message}
        </ToastTitle>
      </Toast>,
      { intent: type ? type : "success" }
    );

  const resetAll = () => {
    setSelectedClient([]);
    setSelectedTemplateCode([]);
    setSelectedTemplate("");
    setSelectedOptions([]);
    setCreateContactHistory(false);
    setContactHistoryNote("");
    setIsNoteTouched(false); // Reset touch state tracking
  };

  const handleContactHistoryToggle = useCallback(
    (_: any, data: { checked: boolean | "mixed" }) => {
      const checked = data.checked === true;
      setCreateContactHistory(checked);
      if (!checked) {
        setContactHistoryNote("");
        setIsNoteTouched(false); // Clear status if unticked
      }
    },
    []
  );

  const handleNoteChange = useCallback((value: string) => {
    setContactHistoryNote(value);
  }, []);

  // 2. Callback executed when user pulls focus (clicks away) out of the input box
  const handleNoteBlur = useCallback(() => {
    setIsNoteTouched(true);
  }, []);

  const noteCharCount = stripHtml(contactHistoryNote).trim().length;

  // 3. Updated check condition: Only marks empty as error if explicitly touched/blurred out
  const noteError =
    createContactHistory && isNoteTouched && noteCharCount === 0
      ? "Contact history remarks are required"
      : noteCharCount > MAX_NOTE_LENGTH
        ? `Must not exceed ${MAX_NOTE_LENGTH} characters`
        : "";

  // 4. Form Submission Block Validation Logic Check
  const isGenerateDisabled =
    !(selectedOptions?.length > 0 && selectedTemplate?.length > 0) ||
    (createContactHistory && noteCharCount === 0) ||
    noteCharCount > MAX_NOTE_LENGTH;

  const getLetterList = () => {
    setTemplatesLoading(true);
    apiServices.TemplateEngine.getLetterTemplate()
      .then((res) => {
        const filteredTemplates = (res.data ?? [])
          .filter((item: any) => item.status === "Completed")
          .map((item: any) => ({
            title: item.title,
            code: item.code,
            description: item.description,
          }));
        setTemplateListing(filteredTemplates);
      })
      .catch(() => {})
      .finally(() => {
        setTemplatesLoading(false);
      });
  };

  useEffect(() => {
    getLetterList();
  }, []);

  const handleTemplateClick = (title: string, code: string) => {
    setSelectedTemplate(title);
    setSelectedTemplateCode(code);
  };

  const handleClientSelection = (selectedClients: selectedValue[]) => {
    setSelectedOptions(selectedClients.map((client) => client.customer_id));
    setSelectedClient(selectedClients);
  };

  let timestamp: any = new Date();
  timestamp = timestamp.toISOString();
  const generateNonce = () => {
    const CryptoJS = require("crypto-js");
    const requestData = {};
    const hash = CryptoJS.SHA256(`${requestData}${timestamp}`);
    return hash.toString(CryptoJS.enc.Hex);
  };
  const nonce = generateNonce();

  const generateLetter = async () => {
    try {
      setLoading(true);
      const requestData: any = {
        customerIds: selectedOptions,
        reportCode: selectedTemplateCode,
      };
      createContactHistory && (requestData["createNote"] = true);
      createContactHistory && (requestData["noteText"] = contactHistoryNote);

      const token = store.getState().authUser.userToken;
      const userId = store.getState().authUser.userId;
      const refreshToken = store.getState().authUser.refreshToken;

      const fileExtension = "zip";
      // const url = `${process.env.REACT_APP_BASE_URL}report/new/custom/${selectedTemplateCode}`;
      const url = `${process.env.REACT_APP_BASE_URL}orchestration/letters/generate`;

      const response = await axios.post(url, requestData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Nonce": nonce,
          "X-Timestamp": timestamp,
          UserId: userId,
          RefreshToken: refreshToken,
        },
        responseType: "blob",
      }).then((response) => {
        // const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement("a");
        // link.href = fileUrl;
        // link.setAttribute("download", `${selectedTemplateCode}.${fileExtension}`);

        // document.body.appendChild(link);
        // link.click();

        notify("Letter generation has been initiated successfully.", "success");

        // window.URL.revokeObjectURL(fileUrl);
        // document.body.removeChild(link);
      })
        .catch((error: any) => {
          notify(error.message, "error");
          console.error("Error generating the letter:", error);
        })
        .finally(() => {
          setLoading(false);
          resetAll();
        });

      // const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
      // const link = document.createElement("a");
      // link.href = fileUrl;
      // link.setAttribute("download", `${selectedTemplateCode}.${fileExtension}`);

      // document.body.appendChild(link);
      // link.click();
      // notify("Letter Generated Successfully", "success");

      // window.URL.revokeObjectURL(fileUrl);
      // document.body.removeChild(link);
    } catch (error: any) {
      notify(error.message, "error");
      console.error("Error generating the letter:", error);
    } finally {
      setLoading(false);
      resetAll();
    }
  };

  const columnsData: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "title",
      header: "Title",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "signedUrl",
      header: "Link",
      Cell: ({ row }: any) => (
        <>
          <a href={row.original.signedUrl} style={{ textDecoration: "none" }}>
            {row.original.title} - {row.original.templateFilePath}
          </a>{" "}
        </>
      ),
    },
  ];

  const handleRowSelection = (row: any) => {
    setSelectedRow(row.original);
  };

  const getClientPlaceholder = () => {
    if (selectedClient.length === 0) {
      return "Select a Client / Multiple Clients";
    } else if (selectedClient.length === 1) {
      return `${selectedClient[0].first_names} ${selectedClient[0].last_name}`;
    } else if (selectedClient.length === 2) {
      return `${selectedClient[0].first_names} ${selectedClient[0].last_name}, ${selectedClient[1].first_names} ${selectedClient[1].last_name}`;
    } else {
      return `${selectedClient[0].first_names}, ${selectedClient[1].first_names} + ${selectedClient.length - 2} others`;
    }
  };

  return (
    <div>
      <OverlayLoader isLoading={loading} />
      <Toaster
        toasterId={toasterId}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        timeout={1000}
      />
      <div className="main-container">
        <div style={{ maxWidth: 420, display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── Select Template ── */}
          <Field label="Select Template :">
            <div
              role="button"
              tabIndex={loading ? -1 : 0}
              onClick={() => !loading && setShowTemplateModal(true)}
              onKeyDown={(e) =>
                e.key === "Enter" && !loading && setShowTemplateModal(true)
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minHeight: 36,
                padding: "0 10px",
                border: "1px solid #d1d9e6",
                borderRadius: 5,
                background: "#fff",
                cursor: loading ? "not-allowed" : "pointer",
                width: 420,
                opacity: loading ? 0.6 : 1,
                userSelect: "none",
                transition: "border-color 0.15s",
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontSize: 12.5,
                  color: selectedTemplate ? "#1a202c" : "#9aa5b8",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedTemplate || "— Select a template —"}
              </span>
              <span
                style={{
                  display: "inline-block",
                  width: 1,
                  height: 18,
                  background: "#d1d9e6",
                  margin: "0 2px",
                  flexShrink: 0,
                }}
              />
              {loading ? (
                <Spinner size="tiny" />
              ) : (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  style={{ color: "#9aa5b8", flexShrink: 0 }}
                >
                  <path
                    d="M2.5 4.5L6 8l3.5-3.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </Field>

          {/* ── Select Client(s) ── */}
          <Field label="Select Client(s) :">
            <Dropdown
              style={{ width: "420px" }}
              placeholder={getClientPlaceholder()}
              onClick={() => setShowClientSelection(true)}
              disabled={selectedTemplate.length === 0}
            />
          </Field>

          {/* ── Contact History ── */}
          <div>
            <Checkbox
              checked={createContactHistory}
              onChange={handleContactHistoryToggle}
              label="Create contact history entry for letter?"
            />

            {createContactHistory && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#242424" }}>
                    Contact History Remarks <span style={{ color: "#dc2626" }}>*</span>
                  </span>
                  <span
                    style={{
                      fontSize: 11.5,
                      color: noteCharCount > MAX_NOTE_LENGTH ? "#dc2626" : "#9aa5b8",
                    }}
                  >
                    {noteCharCount} / {MAX_NOTE_LENGTH}
                  </span>
                </div>

                <Field
                  validationState={noteError ? "error" : "none"}
                  validationMessage={noteError}
                >
                  {/* Added an onBlur intercept wrapper to update interaction states safely */}
                  <div
                    onBlur={handleNoteBlur}
                    style={{
                      border: `1px solid ${noteError ? "#dc2626" : "#d1d9e6"}`,
                      borderRadius: 5,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <TextEditor
                      inputData={contactHistoryNote}
                      onChange={handleNoteChange}
                    />
                  </div>
                </Field>
              </div>
            )}
          </div>

          {/* ── Action row ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 2 }}>
            <ASCButton
              shape="rounded"
              appearance="secondary"
              onClick={resetAll}
              label="Cancel"
              disabled={loading}
            />
            <ASCButton
              shape="rounded"
              appearance="primary"
              className="asc-button-primary"
              onClick={generateLetter}
              label="Generate"
              disabled={isGenerateDisabled || loading}
            />
          </div>

        </div>
      </div>

      {/* Client Selection Modal Dialog */}
      <Dialog
        modalType="alert"
        open={showClientSelection}
        onOpenChange={(event, data) => setShowClientSelection(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <span className="mui-table">Client Selection</span>
            <DialogContent>
              <ClientSelection
                onSelect={handleClientSelection}
                handleClientSelection={handleClientSelection}
                setClientList={setClients}
              />
            </DialogContent>
            <DialogActions>
              <ASCButton
                shape="rounded"
                size="medium"
                appearance="secondary"
                onClick={() => {
                  setShowClientSelection(false);
                  setSelectedClient([]);
                  setSelectedOptions([]);
                }}
                label="Close"
              />
              <ASCButton
                shape="rounded"
                className="asc-button-primary"
                appearance="primary"
                onClick={() => setShowClientSelection(false)}
                size="medium"
                label="Select"
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Template Selection Modal */}
      <TemplateSelectionModal
        open={showTemplateModal}
        templates={templateListing}
        isTemplatesLoading={templatesLoading}
        selectedTitle={selectedTemplate}
        onSelect={(title, code) => {
          handleTemplateClick(title, code);
          setShowTemplateModal(false);
        }}
        onClose={() => setShowTemplateModal(false)}
      />

      {/* Sample Templates Modal Dialog */}
      <Dialog modalType="alert" open={openSampleTemplate}>
        <DialogSurface>
          <DialogBody>
            <span style={{ fontWeight: 600, fontSize: "16px" }}>
              Sample Templates{" "}
            </span>
            <DialogContent>
              <MaterialReactTable
                columns={columnsData}
                data={sampleTemplate}
                manualPagination
                enableGlobalFilter={true}
                enableColumnActions={false}
                enableColumnFilters={true}
                enableRowSelection
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => handleRowSelection(row),
                })}
                muiTableBodyCellProps={() => ({
                  style: {
                    padding: "6px 12px",
                    fontSize: "0.875rem",
                  },
                })}
                enableMultiRowSelection={false}
                enableFullScreenToggle={false}
                muiTablePaginationProps={{
                  showFirstButton: false,
                  showLastButton: false,
                }}
                onGlobalFilterChange={(text: string) => {
                  if (text) {
                    setSearchText(text);
                  } else {
                    setSearchText("");
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <ASCButton
                shape="rounded"
                size="medium"
                onClick={() => {
                  setOpenSampleTemplate(false);
                  setSelectedRow(null);
                }}
                label="Close"
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default LetterGeneration;